import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { WorkspaceItem } from '@telespot/domain';
import { UserMembership } from '@telespot/management/use-cases';
import { WorkspaceMemberData } from './workspace-members.state';

export interface UserMembershipsStateModel {
  organizationId: string | undefined;
  activeUser: WorkspaceMemberData | undefined;

  activeUserMemberships: EntityState<UserMembership>;
  membershipsToAdd: EntityState<UserMembership>;
  membershipsToRemove: EntityState<UserMembership>;

  selectedWorkspacesCount: number;
  addedToWorkspaces: boolean;
  removedFromWorkspaces: boolean;
  loadingMemberships: boolean;
  membershipOperationError: string | undefined;
  loadError: string | undefined;
}

@Injectable()
export class UserMembershipsState extends ComponentStore<UserMembershipsStateModel> {
  private readonly membershipsAdapter: EntityAdapter<UserMembership>;

  constructor() {
    const adapter = createEntityAdapter<UserMembership>({
      selectId: (membership) => membership.workspaceId,
    });

    super({
      organizationId: undefined,
      activeUser: undefined,
      activeUserMemberships: adapter.getInitialState(),
      membershipsToAdd: adapter.getInitialState(),
      membershipsToRemove: adapter.getInitialState(),
      selectedWorkspacesCount: 0,
      addedToWorkspaces: true,
      removedFromWorkspaces: true,
      loadingMemberships: false,
      membershipOperationError: undefined,
      loadError: undefined,
    });

    this.membershipsAdapter = adapter;
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly organizationId$ = this.select((state) => state.organizationId);
  public readonly activeUser$ = this.select((state) => state.activeUser);
  public readonly activeUserMemberships$ = this.select((state) =>
    this.membershipsAdapter.getSelectors().selectAll(state.activeUserMemberships)
  );
  public readonly membershipsToAdd$ = this.select((state) =>
    this.membershipsAdapter.getSelectors().selectAll(state.membershipsToAdd)
  );
  public readonly membershipsToRemove$ = this.select((state) =>
    this.membershipsAdapter.getSelectors().selectAll(state.membershipsToRemove)
  );
  public readonly addedToWorkspaces$ = this.select((state) => state.addedToWorkspaces);
  public readonly removedFromWorkspaces$ = this.select((state) => state.removedFromWorkspaces);
  public readonly loadingMemberships$ = this.select((state) => state.loadingMemberships);
  public readonly membershipOperationError$ = this.select((state) => state.membershipOperationError);
  public readonly loadError$ = this.select((state) => state.loadError);

  public readonly activeMembershipsCount$ = this.select(
    this.activeUserMemberships$,
    (memberships) => memberships.length
  );

  public readonly selectedWorkspaces$ = this.select(
    this.activeUserMemberships$,
    this.membershipsToAdd$,
    this.membershipsToRemove$,
    (activeUserMemberships, membershipsToAdd, membershipsToRemove) => {
      const selectedMemberships = [...activeUserMemberships, ...membershipsToAdd].filter(
        (membership) => !membershipsToRemove.some((m) => m.workspaceId === membership.workspaceId)
      );
      return selectedMemberships.map((membership) => ({
        id: membership.workspaceId,
        name: membership.workspaceName,
      }));
    }
  );

  public readonly selectedWorkspacesCount$ = this.select(
    this.selectedWorkspaces$,
    (selectedWorkspaces) => selectedWorkspaces.length
  );

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setOrganizationId = this.updater((state, organizationId: string) => ({
    ...state,
    organizationId,
  }));

  public readonly setActiveUser = this.updater(
    (state, { activeUser, clearMemberships }: { activeUser: WorkspaceMemberData; clearMemberships: boolean }) => ({
      ...state,
      activeUser,
      activeUserMemberships: this.membershipsAdapter.removeAll(state.activeUserMemberships),
      membershipsToAdd: clearMemberships
        ? this.membershipsAdapter.removeAll(state.membershipsToAdd)
        : state.membershipsToAdd,
      membershipsToRemove: clearMemberships
        ? this.membershipsAdapter.removeAll(state.membershipsToRemove)
        : state.membershipsToRemove,
      selectedWorkspacesCount: 0,
      addedToWorkspaces: true,
      removedFromWorkspaces: true,
      loadingMemberships: false,
      membershipOperationError: undefined,
    })
  );

  public readonly addToWorkspaces = this.updater((state, workspaces: WorkspaceItem[]) => {
    const membershipsToAdd: UserMembership[] = workspaces.map((workspace) => ({
      workspaceId: workspace.id,
      workspaceName: workspace.name,
      membershipId: undefined,
    }));

    const designatedForRemoval = membershipsToAdd
      .filter((membership) => !!state.membershipsToRemove.entities[membership.workspaceId])
      .map((membership) => membership.workspaceId);

    const trulyNewMemberships = membershipsToAdd.filter(
      (membership) =>
        !state.membershipsToRemove.entities[membership.workspaceId] &&
        !state.activeUserMemberships.entities[membership.workspaceId]
    );

    const hasPendingAdditions = trulyNewMemberships.length > 0 || state.membershipsToAdd.ids.length > 0;
    const hasPendingRemovals = state.membershipsToRemove.ids.length > designatedForRemoval.length;

    return {
      ...state,
      addedToWorkspaces: !hasPendingAdditions,
      removedFromWorkspaces: !hasPendingRemovals,
      membershipsToAdd: this.membershipsAdapter.addMany(trulyNewMemberships, state.membershipsToAdd),
      membershipsToRemove: this.membershipsAdapter.removeMany(designatedForRemoval, state.membershipsToRemove),
      selectedWorkspacesCount: state.selectedWorkspacesCount + trulyNewMemberships.length - designatedForRemoval.length,
    };
  });

  public readonly removeFromWorkspaces = this.updater((state, userMemberships: WorkspaceItem[]) => {
    const activeMembers = this.membershipsAdapter.getSelectors().selectAll(state.activeUserMemberships);
    const membershipsToRemove = activeMembers.filter((membership) =>
      userMemberships.some((workspace) => workspace.id === membership.workspaceId)
    );

    const pendingActivationMembers = this.membershipsAdapter.getSelectors().selectAll(state.membershipsToAdd);
    const designatedForAddition = pendingActivationMembers
      .filter((membership) => userMemberships.some((workspace) => workspace.id === membership.workspaceId))
      .map((membership) => membership.workspaceId);

    const hasPendingAdditions = state.membershipsToAdd.ids.length > designatedForAddition.length;
    const hasPendingRemovals = membershipsToRemove.length > 0 || state.membershipsToRemove.ids.length > 0;

    return {
      ...state,
      addedToWorkspaces: !hasPendingAdditions,
      removedFromWorkspaces: !hasPendingRemovals,
      membershipsToRemove: this.membershipsAdapter.addMany(membershipsToRemove, state.membershipsToRemove),
      membershipsToAdd: this.membershipsAdapter.removeMany(designatedForAddition, state.membershipsToAdd),
      selectedWorkspacesCount:
        state.selectedWorkspacesCount - membershipsToRemove.length - designatedForAddition.length,
    };
  });

  public readonly setInitialWorkspaces = this.updater((state, { memberships }: { memberships: UserMembership[] }) => ({
    ...state,
    activeUserMemberships: this.membershipsAdapter.setAll(memberships, state.activeUserMemberships),
    membershipsToAdd: this.membershipsAdapter.removeAll(state.membershipsToAdd),
    membershipsToRemove: this.membershipsAdapter.removeAll(state.membershipsToRemove),
    selectedWorkspacesCount: memberships.length,
    addedToWorkspaces: true,
    removedFromWorkspaces: true,
    loadingMemberships: false,
  }));

  public readonly confirmMembershipsAdded = this.updater((state, memberships: UserMembership[]) => ({
    ...state,
    activeUserMemberships: this.membershipsAdapter.addMany(memberships, state.activeUserMemberships),
    membershipsToAdd: this.membershipsAdapter.removeMany(
      memberships.map((m) => m.workspaceId),
      state.membershipsToAdd
    ),
    addedToWorkspaces: true,
  }));

  public readonly confirmMembershipsRemoved = this.updater((state, membershipIds: string[]) => ({
    ...state,
    activeUserMemberships: this.membershipsAdapter.removeMany(membershipIds, state.activeUserMemberships),
    membershipsToRemove: this.membershipsAdapter.removeMany(membershipIds, state.membershipsToRemove),
    removedFromWorkspaces: true,
  }));

  public readonly setLoadingMemberships = this.updater((state, loadingMemberships: boolean) => ({
    ...state,
    loadingMemberships,
  }));

  public readonly setMembershipOperationError = this.updater(
    (state, membershipOperationError: string | undefined) => ({
      ...state,
      membershipOperationError,
    })
  );

  public readonly setLoadError = this.updater((state, loadError: string | undefined) => ({
    ...state,
    loadError,
    loadingMemberships: false,
  }));

  public readonly resetMembershipState = this.updater((state) => ({
    ...state,
    activeUser: undefined,
    activeUserMemberships: this.membershipsAdapter.removeAll(state.activeUserMemberships),
    membershipsToAdd: this.membershipsAdapter.removeAll(state.membershipsToAdd),
    membershipsToRemove: this.membershipsAdapter.removeAll(state.membershipsToRemove),
    selectedWorkspacesCount: 0,
    addedToWorkspaces: true,
    removedFromWorkspaces: true,
    loadingMemberships: false,
    membershipOperationError: undefined,
  }));
}
